/**
 * GeneralBrandingTab Component
 * Manages general brand settings including name, colors, logos, and fonts
 */

import React, { useState, useRef } from 'react';
import {
  Building2,
  Palette,
  Type,
  Image,
  Upload,
  Trash2,
  Sun,
  Moon,
  RefreshCw,
  Save,
  AlertCircle,
} from 'lucide-react';
import { ColorPicker } from '../../ui/ColorPicker';
import { OrganizationBranding, brandingAPI } from '@/services/api/branding';

interface GeneralBrandingTabProps {
  branding: Partial<OrganizationBranding>;
  onChange: (updates: Partial<OrganizationBranding>) => void;
  onSave: () => Promise<void>;
  saving: boolean;
  organizationName: string;
  organizationId: number;
}

const FONT_OPTIONS = [
  { value: 'Inter, system-ui, sans-serif', label: 'Inter (Default)' },
  { value: 'Roboto, system-ui, sans-serif', label: 'Roboto' },
  { value: 'Open Sans, system-ui, sans-serif', label: 'Open Sans' },
  { value: 'Lato, system-ui, sans-serif', label: 'Lato' },
  { value: 'Poppins, system-ui, sans-serif', label: 'Poppins' },
  { value: 'Montserrat, system-ui, sans-serif', label: 'Montserrat' },
  { value: 'Source Sans Pro, system-ui, sans-serif', label: 'Source Sans Pro' },
  { value: 'Nunito, system-ui, sans-serif', label: 'Nunito' },
  { value: 'Georgia, serif', label: 'Georgia (Serif)' },
  { value: 'Times New Roman, serif', label: 'Times New Roman (Serif)' },
];

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];

export function GeneralBrandingTab({
  branding,
  onChange,
  onSave,
  saving,
  organizationName,
  organizationId,
}: GeneralBrandingTabProps) {
  const [previewMode, setPreviewMode] = useState<'light' | 'dark'>('light');
  const [uploadingField, setUploadingField] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set());

  const logoInputRef = useRef<HTMLInputElement>(null);
  const logoDarkInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (field: keyof OrganizationBranding, value: any) => {
    onChange({ [field]: value });
  };

  const handleFileUpload = async (
    file: File,
    assetType: string,
    fieldName: keyof OrganizationBranding
  ) => {
    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(`File too large. Maximum size is 5MB.`);
      return;
    }

    // Validate file type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setUploadError(`Invalid file type. Please upload PNG, JPEG, GIF, WebP, or SVG.`);
      return;
    }

    setUploadError(null);
    setUploadingField(fieldName as string);

    try {
      const asset = await brandingAPI.uploadAsset(organizationId, assetType, file, file.name);
      handleChange(fieldName, asset.file_url);
      // Clear any broken-image flag so the new image renders
      setBrokenImages(prev => { const s = new Set(prev); s.delete(fieldName as string); return s; });
      // The backend auto-updates the branding record on upload, so no separate save needed
    } catch (err: any) {
      console.error('Upload error:', err);
      // Handle FastAPI validation errors which can be objects or arrays
      const errorDetail = err.response?.data?.detail;
      let errorMessage = 'Failed to upload file';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        // FastAPI validation errors come as array of {loc, msg, type}
        errorMessage = errorDetail.map((e: any) => e.msg || e.message || String(e)).join(', ');
      } else if (errorDetail && typeof errorDetail === 'object') {
        errorMessage = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
      }
      setUploadError(errorMessage);
    } finally {
      setUploadingField(null);
    }
  };

  const handleFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    assetType: string,
    fieldName: keyof OrganizationBranding
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file, assetType, fieldName);
    }
    // Reset input
    e.target.value = '';
  };

  return (
    <div className="space-y-8">
      {/* Brand Identity Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <Building2 className="w-5 h-5 mr-2 text-blue-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Brand Identity</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Brand Name
            </label>
            <input
              type="text"
              value={branding.brand_name || ''}
              onChange={(e) => handleChange('brand_name', e.target.value)}
              placeholder={organizationName}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              Display name used in communications. Leave blank to use the organization name.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tagline
            </label>
            <input
              type="text"
              value={branding.brand_tagline || ''}
              onChange={(e) => handleChange('brand_tagline', e.target.value)}
              placeholder="Your mission in a few words"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Upload Error */}
      {uploadError && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {uploadError}
          <button onClick={() => setUploadError(null)} className="ml-auto text-red-400 hover:text-red-600">
            &times;
          </button>
        </div>
      )}

      {/* Logo Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <Image className="w-5 h-5 mr-2 text-purple-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Logos</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Primary Logo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Primary Logo
            </label>
            <div
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 transition-colors"
              onClick={() => logoInputRef.current?.click()}
            >
              {uploadingField === 'brand_logo_url' ? (
                <div className="py-4">
                  <RefreshCw className="w-8 h-8 mx-auto text-blue-500 animate-spin mb-2" />
                  <p className="text-sm text-gray-500">Uploading...</p>
                </div>
              ) : branding.brand_logo_url ? (
                <div className="relative group">
                  {!brokenImages.has('brand_logo_url') && (
                    <img
                      src={branding.brand_logo_url}
                      alt="Primary Logo"
                      className="max-h-20 mx-auto object-contain"
                      onError={() => setBrokenImages(prev => new Set(prev).add('brand_logo_url'))}
                    />
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleChange('brand_logo_url', ''); setBrokenImages(prev => { const s = new Set(prev); s.delete('brand_logo_url'); return s; }); }}
                    className="absolute top-0 right-0 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                  {brokenImages.has('brand_logo_url') && (
                    <p className="text-xs text-amber-500 mt-1">Logo failed to load — please re-upload</p>
                  )}
                </div>
              ) : (
                <div className="py-4">
                  <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">Click to upload logo</p>
                  <p className="text-xs text-gray-400 mt-1">PNG, JPEG, SVG up to 5MB</p>
                </div>
              )}
            </div>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
              onChange={(e) => handleFileSelect(e, 'logo', 'brand_logo_url')}
              className="hidden"
            />
          </div>

          {/* Dark Mode Logo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Dark Mode Logo
            </label>
            <div
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center bg-gray-900 cursor-pointer hover:border-blue-400 transition-colors"
              onClick={() => logoDarkInputRef.current?.click()}
            >
              {uploadingField === 'brand_logo_dark_url' ? (
                <div className="py-4">
                  <RefreshCw className="w-8 h-8 mx-auto text-blue-500 animate-spin mb-2" />
                  <p className="text-sm text-gray-400">Uploading...</p>
                </div>
              ) : branding.brand_logo_dark_url ? (
                <div className="relative group">
                  {!brokenImages.has('brand_logo_dark_url') && (
                    <img
                      src={branding.brand_logo_dark_url}
                      alt="Dark Logo"
                      className="max-h-20 mx-auto object-contain"
                      onError={() => setBrokenImages(prev => new Set(prev).add('brand_logo_dark_url'))}
                    />
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleChange('brand_logo_dark_url', ''); setBrokenImages(prev => { const s = new Set(prev); s.delete('brand_logo_dark_url'); return s; }); }}
                    className="absolute top-0 right-0 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                  {brokenImages.has('brand_logo_dark_url') && (
                    <p className="text-xs text-amber-500 mt-1">Logo failed to load — please re-upload</p>
                  )}
                </div>
              ) : (
                <div className="py-4">
                  <Moon className="w-8 h-8 mx-auto text-gray-500 mb-2" />
                  <p className="text-sm text-gray-400">Click to upload dark logo</p>
                  <p className="text-xs text-gray-500 mt-1">PNG, JPEG, SVG up to 5MB</p>
                </div>
              )}
            </div>
            <input
              ref={logoDarkInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
              onChange={(e) => handleFileSelect(e, 'logo_dark', 'brand_logo_dark_url')}
              className="hidden"
            />
          </div>

          {/* Favicon */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Favicon
            </label>
            <div
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 transition-colors"
              onClick={() => faviconInputRef.current?.click()}
            >
              {uploadingField === 'brand_favicon_url' ? (
                <div className="py-4">
                  <RefreshCw className="w-8 h-8 mx-auto text-blue-500 animate-spin mb-2" />
                  <p className="text-sm text-gray-500">Uploading...</p>
                </div>
              ) : branding.brand_favicon_url ? (
                <div className="relative group">
                  {!brokenImages.has('brand_favicon_url') && (
                    <img
                      src={branding.brand_favicon_url}
                      alt="Favicon"
                      className="w-12 h-12 mx-auto object-contain"
                      onError={() => setBrokenImages(prev => new Set(prev).add('brand_favicon_url'))}
                    />
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleChange('brand_favicon_url', ''); setBrokenImages(prev => { const s = new Set(prev); s.delete('brand_favicon_url'); return s; }); }}
                    className="absolute top-0 right-0 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                  {brokenImages.has('brand_favicon_url') && (
                    <p className="text-xs text-amber-500 mt-1">Favicon failed to load — please re-upload</p>
                  )}
                </div>
              ) : (
                <div className="py-4">
                  <div className="w-8 h-8 mx-auto bg-gray-200 dark:bg-gray-600 rounded mb-2" />
                  <p className="text-sm text-gray-500">Click to upload favicon</p>
                  <p className="text-xs text-gray-400 mt-1">32x32 or 64x64 PNG/ICO</p>
                </div>
              )}
            </div>
            <input
              ref={faviconInputRef}
              type="file"
              accept="image/png,image/x-icon,image/vnd.microsoft.icon,image/ico"
              onChange={(e) => handleFileSelect(e, 'favicon', 'brand_favicon_url')}
              className="hidden"
            />
          </div>
        </div>
      </div>

      {/* Colors Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Palette className="w-5 h-5 mr-2 text-pink-600" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Brand Colors</h3>
          </div>

          {/* Preview Toggle */}
          <div className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setPreviewMode('light')}
              className={`p-2 rounded ${previewMode === 'light' ? 'bg-white dark:bg-gray-600 shadow' : ''}`}
            >
              <Sun className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPreviewMode('dark')}
              className={`p-2 rounded ${previewMode === 'dark' ? 'bg-white dark:bg-gray-600 shadow' : ''}`}
            >
              <Moon className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <ColorPicker
            label="Primary Color"
            description="Main brand color for buttons, links"
            value={branding.brand_primary_color || '#2563EB'}
            onChange={(color) => handleChange('brand_primary_color', color)}
          />

          <ColorPicker
            label="Secondary Color"
            description="Complementary color for accents"
            value={branding.brand_secondary_color || '#1E40AF'}
            onChange={(color) => handleChange('brand_secondary_color', color)}
          />

          <ColorPicker
            label="Accent Color"
            description="Highlight color for special elements"
            value={branding.brand_accent_color || '#3B82F6'}
            onChange={(color) => handleChange('brand_accent_color', color)}
          />

          <ColorPicker
            label="Text Color"
            description="Primary text color"
            value={branding.brand_text_color || '#1F2937'}
            onChange={(color) => handleChange('brand_text_color', color)}
          />

          <ColorPicker
            label="Page Background"
            description="Main content area background"
            value={branding.brand_background_color || '#FFFFFF'}
            onChange={(color) => handleChange('brand_background_color', color)}
          />

          <ColorPicker
            label="Sidebar Color"
            description="Sidebar background tint color"
            value={branding.brand_sidebar_color || branding.brand_primary_color || '#2563EB'}
            onChange={(color) => handleChange('brand_sidebar_color', color)}
          />
        </div>

        {/* Color Preview */}
        <div className="mt-6 p-4 rounded-lg" style={{
          backgroundColor: previewMode === 'light'
            ? (branding.brand_background_color || '#FFFFFF')
            : '#1F2937',
        }}>
          <h4
            className="text-lg font-semibold mb-2"
            style={{
              color: previewMode === 'light'
                ? (branding.brand_text_color || '#1F2937')
                : '#FFFFFF',
            }}
          >
            Preview: {branding.brand_name || organizationName}
          </h4>
          <p
            className="text-sm mb-4"
            style={{
              color: previewMode === 'light'
                ? (branding.brand_text_color || '#1F2937')
                : '#D1D5DB',
            }}
          >
            {branding.brand_tagline || 'Your organization tagline'}
          </p>
          <div className="flex space-x-3">
            <button
              className="px-4 py-2 rounded-lg text-white text-sm font-medium"
              style={{ backgroundColor: branding.brand_primary_color || '#2563EB' }}
            >
              Primary Button
            </button>
            <button
              className="px-4 py-2 rounded-lg text-white text-sm font-medium"
              style={{ backgroundColor: branding.brand_secondary_color || '#1E40AF' }}
            >
              Secondary Button
            </button>
            <a
              href="#"
              className="px-4 py-2 text-sm font-medium"
              style={{ color: branding.brand_accent_color || '#3B82F6' }}
            >
              Link Text
            </a>
          </div>
        </div>
      </div>

      {/* Typography Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <Type className="w-5 h-5 mr-2 text-green-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Typography</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Body Font
            </label>
            <select
              value={branding.brand_font_family || 'Inter, system-ui, sans-serif'}
              onChange={(e) => handleChange('brand_font_family', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {FONT_OPTIONS.map((font) => (
                <option key={font.value} value={font.value}>
                  {font.label}
                </option>
              ))}
            </select>
            <p
              className="mt-2 p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm"
              style={{ fontFamily: branding.brand_font_family || 'Inter, system-ui, sans-serif' }}
            >
              The quick brown fox jumps over the lazy dog.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Heading Font
            </label>
            <select
              value={branding.brand_font_family_heading || 'Inter, system-ui, sans-serif'}
              onChange={(e) => handleChange('brand_font_family_heading', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {FONT_OPTIONS.map((font) => (
                <option key={font.value} value={font.value}>
                  {font.label}
                </option>
              ))}
            </select>
            <p
              className="mt-2 p-2 bg-gray-50 dark:bg-gray-700 rounded text-lg font-bold"
              style={{ fontFamily: branding.brand_font_family_heading || 'Inter, system-ui, sans-serif' }}
            >
              Heading Preview
            </p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={onSave}
          disabled={saving}
          className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </button>
      </div>
    </div>
  );
}

export default GeneralBrandingTab;
